import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { ProductionKeyPerformanceIndicatorEvaluationWithData } from 'src/app/models/production-key-performance-indicator-evaluation.model';
import { TasksReviewedByColleagueKpiData } from 'src/app/models/tasks-reviewed-by-colleague-kpi-data.model';
import { SecondsToHourStringPipe } from 'src/app/pipes/seconds-to-hour-string.pipe';

@Component({
  selector: 'app-tasks-reviewed-by-colleague-details',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tasks-reviewed-by-colleague-details.component.html',
  styleUrl: './tasks-reviewed-by-colleague-details.component.css',
  imports: [DecimalPipe, MatTooltip, SecondsToHourStringPipe],
})
export class TasksReviewedByColleagueDetailsComponent {
  readonly kpiData = input.required<ProductionKeyPerformanceIndicatorEvaluationWithData>();

  readonly detailsData = computed((): TasksReviewedByColleagueKpiData | null => {
    const rawData = this.kpiData().data?.[0]?.data;
    if (TasksReviewedByColleagueDetailsComponent.isTasksReviewedByColleagueKpiData(rawData)) {
      return rawData;
    }

    if (typeof rawData !== 'object' || rawData === null) {
      return null;
    }

    const nestedData = (rawData as { company_product_general_kpi?: { kpi_data?: unknown } }).company_product_general_kpi?.kpi_data;
    return TasksReviewedByColleagueDetailsComponent.isTasksReviewedByColleagueKpiData(nestedData) ? nestedData : null;
  });

  readonly complianceBarWidth = computed((): number => {
    const data = this.detailsData();

    if (data === null || data.total_registered_seconds === 0) {
      return 0;
    }

    return (data.applicable_registered_seconds / data.total_registered_seconds) * 100;
  });

  readonly penalizedBarWidth = computed((): number => Math.max(0, 100 - this.complianceBarWidth()));

  readonly nonApplicableRegisteredSeconds = computed((): number => {
    const data = this.detailsData();

    if (data === null) {
      return 0;
    }

    return Math.max(0, data.total_registered_seconds - data.applicable_registered_seconds);
  });

  readonly applicableSegmentTooltip = computed((): string => {
    const data = this.detailsData();

    if (data === null || data.applicable_registered_seconds === 0) {
      return '';
    }

    const hours = this.formatSeconds(data.applicable_registered_seconds);

    return hours;
  });

  readonly penalizedSegmentTooltip = computed((): string => {
    const seconds = this.nonApplicableRegisteredSeconds();

    if (seconds === 0) {
      return '';
    }

    const hours = this.formatSeconds(seconds);

    return hours;
  });

  private formatSeconds(seconds: number): string {
    return new SecondsToHourStringPipe().transform(seconds);
  }

  public static isTasksReviewedByColleagueKpiData(data: unknown): data is TasksReviewedByColleagueKpiData {
    if (typeof data !== 'object' || data === null) {
      return false;
    }

    const kpiData = data as Partial<TasksReviewedByColleagueKpiData>;

    return (
      typeof kpiData.tasks_evaluated_count === 'number' &&
      typeof kpiData.total_registered_seconds === 'number' &&
      typeof kpiData.applicable_registered_seconds === 'number'
    );
  }
}
